/** SUNTRONIX ONE — Administration → Quote Disclaimer.
 *  Centrally governed, versioned Product Image Disclaimer applied to all
 *  new quotes / revisions / proforma / PDF / customer views. Super-Admin /
 *  Management only. Normal Sales users cannot edit or remove it. */
import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { ShieldCheck, History, Save, RotateCcw } from "lucide-react";

export default function QuoteDisclaimer() {
  const [cur, setCur] = useState(null);
  const [versions, setVersions] = useState([]);
  const [heading, setHeading] = useState("");
  const [text, setText] = useState("");
  const [footer, setFooter] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [c, v] = await Promise.all([
        api.get("/quote-disclaimer"),
        api.get("/quote-disclaimer/versions"),
      ]);
      setCur(c.data); setVersions(v.data.items || []);
      setHeading(c.data.heading || "Product Image Disclaimer");
      setText(c.data.text || ""); setFooter(c.data.footer || "");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not load disclaimer"); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if ((text || "").trim().length < 20) { toast.error("Disclaimer text is too short"); return; }
    setBusy(true);
    try {
      const { data } = await api.put("/quote-disclaimer", { heading, text, footer });
      toast.success(`Saved — now version ${data.version}. Applies to all future quotes.`);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    setBusy(false);
  };

  const restore = async (version) => {
    if (!window.confirm(`Restore version ${version}? This becomes the new active disclaimer for all future quotes.`)) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/quote-disclaimer/rollback/${version}`);
      toast.success(`Restored v${version} → now active as version ${data.version}`);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Restore failed"); }
    setBusy(false);
  };

  const dirty = cur && (text !== cur.text || footer !== (cur.footer || "") || heading !== (cur.heading || ""));

  return (
    <div className="max-w-[900px] mx-auto px-4 md:px-6 py-6 space-y-5" data-testid="quote-disclaimer-page">
      <div className="flex items-center gap-2">
        <ShieldCheck className="h-5 w-5 text-slate-700" />
        <h1 className="text-xl font-semibold text-slate-900">Product Image Disclaimer</h1>
        {cur && <Badge className="text-[11px] border bg-slate-50 text-slate-600 border-slate-200" data-testid="disclaimer-version">v{cur.version}</Badge>}
      </div>
      <p className="text-[13px] text-slate-500">
        Centrally governed disclaimer shown below the product section on every new quotation, revision,
        proforma and PDF. Snapshotted onto each quote at issue — editing here never changes already-issued
        quotes. Sales users cannot remove it.
      </p>

      <div className="rounded-lg border border-slate-200 bg-white px-4 py-4 space-y-3">
        <div>
          <div className="text-[10px] uppercase tracking-wide text-slate-400 mb-1">Heading</div>
          <Input value={heading} onChange={(e) => setHeading(e.target.value)} data-testid="disclaimer-heading-input" />
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wide text-slate-400 mb-1">Disclaimer text (mandatory)</div>
          <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={6}
            className="text-[13px]" data-testid="disclaimer-text-input" />
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wide text-slate-400 mb-1">Compact footer (optional)</div>
          <Textarea value={footer} onChange={(e) => setFooter(e.target.value)} rows={2}
            className="text-[13px]" data-testid="disclaimer-footer-input" />
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" onClick={save} disabled={busy || !dirty} data-testid="disclaimer-save">
            <Save className="h-3.5 w-3.5 mr-1.5" /> Save new version
          </Button>
          {dirty && (
            <Button variant="outline" size="sm" onClick={load} data-testid="disclaimer-reset">
              <RotateCcw className="h-3.5 w-3.5 mr-1.5" /> Discard changes
            </Button>
          )}
        </div>
      </div>

      {/* Live preview */}
      <div className="rounded-lg border border-slate-200 bg-slate-100 px-4 py-3" data-testid="disclaimer-preview">
        <div className="text-[12px] font-semibold text-slate-800">{heading || "Product Image Disclaimer"}</div>
        <div className="text-[11px] text-slate-500 mt-1 leading-relaxed">{text}</div>
      </div>

      {/* Version history */}
      <div>
        <div className="flex items-center gap-2 text-[13px] font-medium text-slate-700 mb-2">
          <History className="h-4 w-4 text-slate-500" /> Version history
        </div>
        <div className="rounded-lg border border-slate-200 bg-white divide-y divide-slate-100" data-testid="disclaimer-versions">
          {versions.map((v) => (
            <div key={v.version} className="px-4 py-2.5 flex items-start gap-3" data-testid={`disclaimer-v-${v.version}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 text-[12px]">
                  <Badge className="text-[10px] border bg-slate-50 text-slate-600 border-slate-200">v{v.version}</Badge>
                  <span className="text-slate-400">{(v.at || "").slice(0, 10)} · {(v.by || "").split("@")[0]}</span>
                  {v.restored_from && <span className="text-[10px] text-slate-400">(restored from v{v.restored_from})</span>}
                  {cur && v.version === cur.version && <Badge className="text-[10px] border bg-emerald-50 text-emerald-700 border-emerald-200">ACTIVE</Badge>}
                </div>
                <div className="text-[11px] text-slate-500 mt-1 line-clamp-2">{v.text}</div>
              </div>
              {cur && v.version !== cur.version && (
                <Button variant="outline" size="sm" onClick={() => restore(v.version)} disabled={busy}
                  data-testid={`disclaimer-restore-${v.version}`} className="shrink-0">
                  <RotateCcw className="h-3.5 w-3.5 mr-1.5" /> Restore
                </Button>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
